import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    Rating,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Chip,
    Avatar,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
    Pagination
} from '@mui/material';
import { getAllLimitedCardsForAdminWithSets, updateLimitedCard } from '../../services/tracker-limited-data';
import { inkColors } from '../LimitedTierlist/constants/inkColors';
import { rarityColors } from '../LimitedTierlist/constants/rarityColors';
import CardPreviewModal from '../LimitedTierlist/CardPreviewModal';
import MobileAdminCardPreview from './MobileAdminCardPreview';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import SetManager from './SetManager';

const LimitedTierlistManager = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [editingCard, setEditingCard] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [previewCard, setPreviewCard] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRarity, setSelectedRarity] = useState('all');
    const [selectedInkColor, setSelectedInkColor] = useState('all');
    const [selectedSet, setSelectedSet] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const cardsPerPage = 100;
    const [showSetManager, setShowSetManager] = useState(false);
    const [hoveredCard, setHoveredCard] = useState(null);
    const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
    const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
    const [mobilePreviewIndex, setMobilePreviewIndex] = useState(0);

    useEffect(() => {
        fetchCards();
    }, []);

    useEffect(() => {
        setCurrentPage(1); // Reset to first page when filters change
    }, [searchTerm, selectedRarity, selectedInkColor, selectedSet]);

    const fetchCards = async () => {
        try {
            setLoading(true);
            const cardsData = await getAllLimitedCardsForAdminWithSets();
            // Mapuj nową strukturę danych na starą kompatybilną strukturę
            const mappedCards = cardsData.map(card => ({
                ...card,
                // Mapuj variants na image_urls dla kompatybilności
                image_urls: card.variants?.map(variant => ({
                    url: variant.detail_image_url
                })) || []
                // set jest już poprawnie ustawiony przez funkcję Firebase
            }));
            setCards(mappedCards);
            setError(null);
        } catch (err) {
            console.error('Błąd podczas pobierania kart:', err);
            console.log('Próbuję fallback - pobieram karty bez setów...');

            try {
                // Fallback - użyj Firebase z kartami połączonymi z setami
                const { getAllLimitedCardsForAdminWithSets } = await import('../../firebase/firestore');
                const fallbackCardsData = await getAllLimitedCardsForAdminWithSets();
                const mappedFallbackCards = fallbackCardsData.map(card => ({
                    ...card,
                    image_urls: card.variants?.map(variant => ({
                        url: variant.detail_image_url
                    })) || []
                }));
                setCards(mappedFallbackCards);
                setError(null);
            } catch (fallbackErr) {
                console.error('Fallback failed:', fallbackErr);
                setError('Nie udało się pobrać kart. Sprawdź połączenie z internetem.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEditCard = (card) => {
        setEditingCard({
            ...card,
            rating: card.rating || 0,
            description: card.description || ''
        });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingCard(null);
    };

    const handlePreviewCard = (card) => {
        setPreviewCard(card);
        setPreviewDialogOpen(true);
    };

    const handleCardHover = (card, event) => {
        setHoveredCard(card);
        setHoverPosition({ x: event.clientX, y: event.clientY });
    };

    const handleCardLeave = () => {
        setHoveredCard(null);
    };

    const handleClosePreviewDialog = () => {
        setPreviewDialogOpen(false);
        setPreviewCard(null);
    };

    const handleMobileCardClick = (card, index) => {
        if (isMobile) {
            // Znajdź indeks karty w całej liście filteredCards
            const globalIndex = filteredCards.findIndex(c => c.id === card.id);
            setMobilePreviewIndex(globalIndex >= 0 ? globalIndex : 0);
            setMobilePreviewOpen(true);
        }
    };

    const handleMobilePreviewClose = () => {
        setMobilePreviewOpen(false);
    };

    const handleMobileIndexChange = (newIndex) => {
        setMobilePreviewIndex(newIndex);
    };

    const handleSaveCard = async (updatedCard) => {
        if (!updatedCard) return;

        try {
            setSaving(true);
            await updateLimitedCard(updatedCard.id, {
                rating: updatedCard.rating,
                description: updatedCard.description
            });

            // Aktualizuj lokalny stan
            setCards(prevCards =>
                prevCards.map(card =>
                    card.id === updatedCard.id
                        ? { ...card, rating: updatedCard.rating, description: updatedCard.description }
                        : card
                )
            );

            setSnackbar({
                open: true,
                message: 'Ocena karty została pomyślnie zapisana!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Błąd podczas zapisywania oceny:', error);
            setSnackbar({
                open: true,
                message: 'Wystąpił błąd podczas zapisywania oceny',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleRatingChange = (newValue) => {
        setEditingCard(prev => ({ ...prev, rating: newValue }));
    };

    const handleDescriptionChange = (event) => {
        setEditingCard(prev => ({ ...prev, description: event.target.value }));
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Filtrowanie kart
    const filteredCards = cards.filter(card => {
        const matchesSearch = card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (card.subtitle && card.subtitle.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesRarity = selectedRarity === 'all' || card.rarity === selectedRarity;
        const matchesInkColor = selectedInkColor === 'all' ||
            (card.magic_ink_colors && card.magic_ink_colors.includes(selectedInkColor));
        const matchesSet = selectedSet === 'all' ||
            (card.set && card.set.name === selectedSet);

        return matchesSearch && matchesRarity && matchesInkColor && matchesSet;
    });

    // Paginacja
    const totalPages = Math.ceil(filteredCards.length / cardsPerPage);
    const startIndex = (currentPage - 1) * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    const currentCards = filteredCards.slice(startIndex, endIndex);

    const getUniqueRarities = () => [...new Set(cards.map(card => card.rarity).filter(Boolean))].sort((a, b) => {
        // Definiujemy kolejność rarity (COMMON, UNCOMMON, RARE, SUPER_RARE, LEGENDARY, SPECIAL)
        const rarityOrder = { 'COMMON': 1, 'UNCOMMON': 2, 'RARE': 3, 'SUPER_RARE': 4, 'LEGENDARY': 5, 'SPECIAL': 6 };
        const aValue = rarityOrder[a] || 999;
        const bValue = rarityOrder[b] || 999;
        return aValue - bValue;
    });
    const getUniqueInkColors = () => [...new Set(cards.flatMap(card => card.magic_ink_colors || []))].sort();
    const getUniqueSets = () => cards
        .map(card => card.set?.name)  // Tylko nazwy setów
        .filter(Boolean)
        .filter((name, index, arr) => arr.indexOf(name) === index)  // Usuń duplikaty
        .sort((a, b) => {
            // Znajdź sort_number dla każdej nazwy setu
            const cardA = cards.find(card => card.set?.name === a);
            const cardB = cards.find(card => card.set?.name === b);
            const sortNumberA = cardA?.set?.sort_number || 0;
            const sortNumberB = cardB?.set?.sort_number || 0;
            return sortNumberA - sortNumberB;
        });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    // Jeśli pokazujemy SetManager, renderuj tylko go
    if (showSetManager) {
        console.log('Rendering SetManager, showSetManager is true');
        return (
            <SetManager onBack={() => {
                console.log('SetManager onBack called, setting showSetManager to false');
                setShowSetManager(false);
            }} />
        );
    }

    console.log('Rendering main content, showSetManager is false');
    return (
        <Box>
            {/* Nagłówek */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h2" sx={{ color: 'white', fontWeight: 600 }}>
                    Zarządzanie Tierlistą Limited
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => {
                        console.log('Zarządzaj Setami clicked, setting showSetManager to true');
                        setShowSetManager(true);
                    }}
                    sx={{
                        borderColor: 'rgba(76, 29, 149, 0.5)',
                        color: 'rgb(196, 181, 253)',
                        '&:hover': {
                            borderColor: 'rgb(196, 181, 253)',
                            backgroundColor: 'rgba(76, 29, 149, 0.1)'
                        }
                    }}
                >
                    Zarządzaj Setami
                </Button>
            </Box>

            {/* Filtry */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(76, 29, 149, 0.3)' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
                    <TextField
                        label="Szukaj karty"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        sx={{ minWidth: 200, '& .MuiInputLabel-root': { color: 'rgb(196, 181, 253)' } }}
                        InputProps={{ sx: { color: 'white' } }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ color: 'rgb(196, 181, 253)' }}>Rzadkość</InputLabel>
                        <Select
                            value={selectedRarity}
                            onChange={(e) => setSelectedRarity(e.target.value)}
                            sx={{ color: 'white' }}
                            label="Rzadkość"
                        >
                            <MenuItem value="all">Wszystkie</MenuItem>
                            {getUniqueRarities().map(rarity => (
                                <MenuItem key={rarity} value={rarity}>{rarity}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ color: 'rgb(196, 181, 253)' }}>Kolor atramentu</InputLabel>
                        <Select
                            value={selectedInkColor}
                            onChange={(e) => setSelectedInkColor(e.target.value)}
                            sx={{ color: 'white' }}
                            label="Kolor atramentu"
                        >
                            <MenuItem value="all">Wszystkie</MenuItem>
                            {getUniqueInkColors().map(color => (
                                <MenuItem key={color} value={color}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar
                                            src={inkColors[color]?.icon}
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                backgroundColor: inkColors[color]?.color,
                                                cursor: 'pointer',
                                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                                '&:hover': {
                                                    border: '2px solid rgba(255, 255, 255, 0.4)',
                                                    transform: 'scale(1.1)',
                                                    transition: 'all 0.2s ease-in-out'
                                                }
                                            }}
                                        />
                                        {inkColors[color]?.name || color}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ color: 'rgb(196, 181, 253)' }}>Set</InputLabel>
                        <Select
                            value={selectedSet}
                            onChange={(e) => setSelectedSet(e.target.value)}
                            sx={{ color: 'white' }}
                            label="Set"
                        >
                            <MenuItem value="all">Wszystkie</MenuItem>
                            {getUniqueSets().map(set => (
                                <MenuItem key={set} value={set}>
                                    {set}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            {/* Tabela kart */}
            <Paper sx={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(76, 29, 149, 0.3)' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(76, 29, 149, 0.15)' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '60px', fontSize: '0.75rem', padding: '8px 4px' }}>Obrazek</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '60px', fontSize: '0.75rem', padding: '8px 4px' }}>Numer</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '200px', fontSize: '0.75rem', padding: '8px 6px' }}>Nazwa karty</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '70px', fontSize: '0.75rem', padding: '8px 4px' }}>Rzadkość</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '70px', fontSize: '0.75rem', padding: '8px 4px' }}>Kolory</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '100px', fontSize: '0.75rem', padding: '8px 4px' }}>Ocena</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '80px', fontSize: '0.75rem', padding: '8px 4px' }}>Akcje</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '100px', fontSize: '0.75rem', padding: '8px 4px' }}>Set</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentCards.map((card, index) => (
                                <TableRow
                                    key={card.id}
                                    sx={{
                                        '&:hover': { backgroundColor: 'rgba(76, 29, 149, 0.08)' },
                                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(76, 29, 149, 0.02)',
                                        transition: 'background-color 0.2s ease-in-out',
                                        '& .MuiTableCell-root': {
                                            padding: '6px 4px',
                                            fontSize: '0.75rem'
                                        }
                                    }}
                                >
                                    <TableCell>
                                        <Tooltip title={`Kliknij aby zobaczyć ${card.name} w pełnym rozmiarze`}>
                                            {card.image_urls && card.image_urls.length > 0 ? (
                                                <Avatar
                                                    src={card.image_urls[0].url}
                                                    variant="rounded"
                                                    onMouseEnter={(e) => !isMobile && handleCardHover(card, e)}
                                                    onMouseLeave={() => !isMobile && handleCardLeave()}
                                                    sx={{
                                                        width: 45,
                                                        height: 63,
                                                        cursor: 'pointer',
                                                        border: '2px solid rgba(76, 29, 149, 0.3)',
                                                        '&:hover': {
                                                            border: '2px solid rgba(76, 29, 149, 0.6)',
                                                            transform: 'scale(1.05)',
                                                            transition: 'all 0.2s ease-in-out'
                                                        }
                                                    }}
                                                    onClick={() => isMobile ? handleMobileCardClick(card, 0) : window.open(card.image_urls[0].url, '_blank')}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: 45,
                                                        height: 63,
                                                        background: 'linear-gradient(135deg, rgba(76, 29, 149, 0.2), rgba(139, 92, 246, 0.2))',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: '2px dashed rgba(76, 29, 149, 0.3)',
                                                        borderRadius: '8px'
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)' }}>
                                                        Brak obrazka
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            color: card.card_identifier ? 'white' : 'rgb(156, 163, 175)'
                                        }}>
                                            {card.card_identifier ? `#${card.card_identifier.split('/')[0]}` : (card.id ? `ID: ${card.id}` : '-')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" sx={{
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                lineHeight: 1.2
                                            }}>
                                                {card.name}
                                            </Typography>
                                            {card.subtitle && (
                                                <Typography variant="caption" sx={{
                                                    color: 'rgb(156, 163, 175)',
                                                    fontSize: '0.75rem',
                                                    fontStyle: 'italic'
                                                }}>
                                                    {card.subtitle}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={card.rarity === 'SPECIAL' && card.special_rarity_id ? `${card.rarity} (${card.special_rarity_id})` : card.rarity}
                                            size="small"
                                            sx={{
                                                background: card.rarity === 'SPECIAL'
                                                    ? 'linear-gradient(135deg, #8b0000, #dc143c, #ff1493)'
                                                    : card.rarity === 'SUPER'
                                                        ? 'linear-gradient(135deg, #a8a8a8, #c0c0c0, #d3d3d3)'
                                                        : card.rarity === 'LEGENDARY'
                                                            ? 'linear-gradient(135deg, #8b6914, #b8860b, #daa520)'
                                                            : card.rarity === 'RARE'
                                                                ? 'linear-gradient(135deg, #8b4513, #cd7f32, #d2691e)'
                                                                : (rarityColors[card.rarity] || 'rgba(156, 163, 175, 0.2)'),
                                                color: card.rarity === 'UNCOMMON' ? '#374151' : 'white',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                minWidth: '80px',
                                                justifyContent: 'center',
                                                border: card.rarity === 'SPECIAL'
                                                    ? '1px solid #dc143c'
                                                    : card.rarity === 'SUPER'
                                                        ? '1px solid #e5e5e5'
                                                        : card.rarity === 'LEGENDARY'
                                                            ? '1px solid #daa520'
                                                            : card.rarity === 'RARE'
                                                                ? '1px solid #d2691e'
                                                                : 'none',
                                                ...(card.rarity === 'SPECIAL' && {
                                                    boxShadow: '0 0 4px rgba(220, 20, 60, 0.4)'
                                                }),
                                                ...(card.rarity === 'SUPER' && {
                                                    boxShadow: '0 0 4px rgba(192, 192, 192, 0.4)'
                                                }),
                                                ...(card.rarity === 'LEGENDARY' && {
                                                    boxShadow: '0 0 4px rgba(184, 134, 11, 0.4)'
                                                }),
                                                ...(card.rarity === 'RARE' && {
                                                    boxShadow: '0 0 3px rgba(205, 127, 50, 0.4)'
                                                })
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                            {card.magic_ink_colors?.map((color, index) => (
                                                inkColors[color] && (
                                                    <Tooltip key={index} title={inkColors[color].name}>
                                                        <Avatar
                                                            src={inkColors[color].icon}
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                                backgroundColor: inkColors[color].color,
                                                                cursor: 'pointer',
                                                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                                                '&:hover': {
                                                                    border: '2px solid rgba(255, 255, 255, 0.4)',
                                                                    transform: 'scale(1.1)',
                                                                    transition: 'all 0.2s ease-in-out'
                                                                }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Rating
                                                value={card.rating || 0}
                                                precision={0.5}
                                                size="small"
                                                readOnly
                                                sx={{
                                                    '& .MuiRating-iconFilled': { color: 'rgb(245, 158, 11)' },
                                                    '& .MuiRating-iconHover': { color: 'rgb(245, 158, 11)' }
                                                }}
                                            />
                                            <Typography variant="caption" sx={{
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: '0.75rem'
                                            }}>
                                                {card.rating || 0}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Tooltip title="Podgląd karty">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => isMobile ? handleMobileCardClick(card, index) : handlePreviewCard(card)}
                                                    sx={{
                                                        color: 'rgb(34, 197, 94)',
                                                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(34, 197, 94, 0.2)'
                                                        }
                                                    }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edytuj ocenę">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditCard(card)}
                                                    sx={{
                                                        color: 'rgb(196, 181, 253)',
                                                        backgroundColor: 'rgba(196, 181, 253, 0.1)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(196, 181, 253, 0.2)'
                                                        }
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{
                                            color: 'white',
                                            textAlign: 'center',
                                            fontWeight: 500,
                                            fontSize: '0.8rem'
                                        }}>
                                            {card.set?.name || '-'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Informacja o liczbie kart i paginacja */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                p: 2,
                backgroundColor: 'rgba(17, 24, 39, 0.8)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '8px'
            }}>
                <Typography variant="body2" sx={{ color: 'rgb(156, 163, 175)' }}>
                    Pokazano {startIndex + 1}-{Math.min(endIndex, filteredCards.length)} z {filteredCards.length} kart
                </Typography>

                {totalPages > 1 && (
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="small"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: 'rgb(196, 181, 253)',
                                '&.Mui-selected': {
                                    backgroundColor: 'rgb(76, 29, 149)',
                                    color: 'white'
                                }
                            }
                        }}
                    />
                )}
            </Box>

            {/* Dialog edycji karty */}
            <Dialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(17, 24, 39, 0.95)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        color: 'white'
                    }
                }}
            >
                <DialogTitle sx={{
                    borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                    backgroundColor: 'rgba(76, 29, 149, 0.1)'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                            Edytuj ocenę karty
                        </Typography>
                        <IconButton onClick={handleCloseEditDialog} sx={{ color: 'rgb(156, 163, 175)' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    {editingCard && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Preview karty */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {editingCard.image_urls && editingCard.image_urls.length > 0 ? (
                                    <Avatar
                                        src={editingCard.image_urls[0].url}
                                        variant="rounded"
                                        sx={{ width: 80, height: 112 }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 112,
                                            background: 'linear-gradient(135deg, rgba(76, 29, 149, 0.2), rgba(139, 92, 246, 0.2))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '2px dashed rgba(76, 29, 149, 0.3)',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)' }}>
                                            Brak obrazka
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ color: 'white' }}>
                                        {editingCard.name}
                                    </Typography>
                                    {editingCard.subtitle && (
                                        <Typography variant="body2" sx={{ color: 'rgb(156, 163, 175)' }}>
                                            {editingCard.subtitle}
                                        </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={`#${editingCard.card_identifier ? editingCard.card_identifier.split('/')[0] : (editingCard.id || 'N/A')}`}
                                            size="small"
                                            sx={{
                                                backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                                color: 'white',
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                        <Chip
                                            label={editingCard.rarity === 'SPECIAL' && editingCard.special_rarity_id ? `${editingCard.rarity} (${editingCard.special_rarity_id})` : editingCard.rarity}
                                            size="small"
                                            sx={{
                                                background: editingCard.rarity === 'SPECIAL'
                                                    ? 'linear-gradient(135deg, #8b0000, #dc143c, #ff1493)'
                                                    : editingCard.rarity === 'SUPER'
                                                        ? 'linear-gradient(135deg, #a8a8a8, #c0c0c0, #d3d3d3)'
                                                        : editingCard.rarity === 'LEGENDARY'
                                                            ? 'linear-gradient(135deg, #8b6914, #b8860b, #daa520)'
                                                            : editingCard.rarity === 'RARE'
                                                                ? 'linear-gradient(135deg, #8b4513, #cd7f32, #d2691e)'
                                                                : (rarityColors[editingCard.rarity] || 'rgba(156, 163, 175, 0.2)'),
                                                color: editingCard.rarity === 'UNCOMMON' ? '#374151' : 'white',
                                                fontSize: '0.75rem',
                                                border: editingCard.rarity === 'SPECIAL'
                                                    ? '1px solid #dc143c'
                                                    : editingCard.rarity === 'SUPER'
                                                        ? '1px solid #e5e5e5'
                                                        : editingCard.rarity === 'LEGENDARY'
                                                            ? '1px solid #daa520'
                                                            : editingCard.rarity === 'RARE'
                                                                ? '1px solid #d2691e'
                                                                : 'none',
                                                ...(editingCard.rarity === 'SPECIAL' && {
                                                    boxShadow: '0 0 4px rgba(220, 20, 60, 0.4)'
                                                }),
                                                ...(editingCard.rarity === 'SUPER' && {
                                                    boxShadow: '0 0 4px rgba(192, 192, 192, 0.4)'
                                                }),
                                                ...(editingCard.rarity === 'LEGENDARY' && {
                                                    boxShadow: '0 0 4px rgba(184, 134, 11, 0.4)'
                                                }),
                                                ...(editingCard.rarity === 'RARE' && {
                                                    boxShadow: '0 0 3px rgba(205, 127, 50, 0.4)'
                                                })
                                            }}
                                        />
                                        {editingCard.set?.name && (
                                            <Chip
                                                label={editingCard.set.name}
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                                    color: 'white',
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Ocena */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ color: 'white', mb: 1 }}>
                                    Ocena (1-5)
                                </Typography>
                                <Rating
                                    value={editingCard.rating}
                                    onChange={(event, newValue) => handleRatingChange(newValue)}
                                    precision={0.5}
                                    size="large"
                                    sx={{
                                        '& .MuiRating-iconFilled': { color: 'rgb(245, 158, 11)' },
                                        '& .MuiRating-iconHover': { color: 'rgb(245, 158, 11)' }
                                    }}
                                />
                                <Typography variant="body2" sx={{ color: 'rgb(156, 163, 175)', mt: 1 }}>
                                    Aktualna ocena: {editingCard.rating || 0}
                                </Typography>
                            </Box>

                            {/* Opis */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ color: 'white', mb: 1 }}>
                                    Opis karty
                                </Typography>
                                <TextField
                                    multiline
                                    rows={4}
                                    fullWidth
                                    value={editingCard.description}
                                    onChange={handleDescriptionChange}
                                    placeholder="Dodaj opis karty (opcjonalnie)"
                                    sx={{
                                        '& .MuiInputLabel-root': { color: 'rgb(196, 181, 253)' },
                                        '& .MuiOutlinedInput-root': {
                                            color: 'white',
                                            '& fieldset': { borderColor: 'rgba(76, 29, 149, 0.5)' },
                                            '&:hover fieldset': { borderColor: 'rgba(76, 29, 149, 0.7)' },
                                            '&.Mui-focused fieldset': { borderColor: 'rgb(76, 29, 149)' }
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ borderTop: '1px solid rgba(76, 29, 149, 0.3)', p: 2 }}>
                    <Button
                        onClick={handleCloseEditDialog}
                        sx={{ color: 'rgb(156, 163, 175)' }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={() => handleSaveCard(editingCard)}
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        sx={{
                            backgroundColor: 'rgb(76, 29, 149)',
                            '&:hover': { backgroundColor: 'rgb(91, 33, 182)' }
                        }}
                    >
                        {saving ? 'Zapisywanie...' : 'Zapisz'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog podglądu karty */}
            <Dialog
                open={previewDialogOpen}
                onClose={handleClosePreviewDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(17, 24, 39, 0.95)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        color: 'white'
                    }
                }}
            >
                <DialogTitle sx={{
                    borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                    backgroundColor: 'rgba(76, 29, 149, 0.1)'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                            Podgląd karty
                        </Typography>
                        <IconButton onClick={handleClosePreviewDialog} sx={{ color: 'rgb(156, 163, 175)' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    {previewCard && (
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                            {/* Obraz karty */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                {previewCard.image_urls && previewCard.image_urls.length > 0 ? (
                                    <Avatar
                                        src={previewCard.image_urls[0].url}
                                        variant="rounded"
                                        sx={{
                                            width: { xs: '100%', md: '300px' },
                                            height: { xs: 'auto', md: '420px' },
                                            maxWidth: '300px'
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: '300px' },
                                            height: { xs: 'auto', md: '420px' },
                                            maxWidth: '300px',
                                            background: 'linear-gradient(135deg, rgba(76, 29, 149, 0.2), rgba(139, 92, 246, 0.2))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '2px dashed rgba(76, 29, 149, 0.3)',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ color: 'rgb(156, 163, 175)' }}>
                                            Brak obrazka
                                        </Typography>
                                    </Box>
                                )}
                                {previewCard.image_urls && previewCard.image_urls.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => window.open(previewCard.image_urls[0].url, '_blank')}
                                        sx={{
                                            borderColor: 'rgb(76, 29, 149)',
                                            color: 'rgb(196, 181, 253)',
                                            '&:hover': { borderColor: 'rgb(196, 181, 253)' }
                                        }}
                                    >
                                        Otwórz w nowej karcie
                                    </Button>
                                )}
                            </Box>

                            {/* Informacje o karcie */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Nazwa i podtytuł */}
                                <Box>
                                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                        {previewCard.name}
                                    </Typography>
                                    {previewCard.subtitle && (
                                        <Typography variant="h6" sx={{ color: 'rgb(156, 163, 175)' }}>
                                            {previewCard.subtitle}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Podstawowe informacje */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: 'rgb(156, 163, 175)', mb: 0.5 }}>
                                            Numer sortowania
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                                            #{previewCard.card_identifier ? previewCard.card_identifier.split('/')[0] : (previewCard.id || '-')}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: 'rgb(156, 163, 175)', mb: 0.5 }}>
                                            Rzadkość
                                        </Typography>
                                        <Chip
                                            label={previewCard.rarity === 'SPECIAL' && previewCard.special_rarity_id ? `${previewCard.rarity} (${previewCard.special_rarity_id})` : previewCard.rarity || '-'}
                                            size="small"
                                            sx={{
                                                background: previewCard.rarity === 'SPECIAL'
                                                    ? 'linear-gradient(135deg, #8b0000, #dc143c, #ff1493)'
                                                    : (rarityColors[previewCard.rarity] || 'rgba(156, 163, 175, 0.2)'),
                                                color: 'white',
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: 'rgb(156, 163, 175)', mb: 0.5 }}>
                                            Kolory atramentu
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {previewCard.magic_ink_colors?.map((color, index) => (
                                                inkColors[color] && (
                                                    <Tooltip key={index} title={inkColors[color].name}>
                                                        <Avatar
                                                            src={inkColors[color].icon}
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                backgroundColor: inkColors[color].color
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )
                                            )) || '-'}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: 'rgb(156, 163, 175)', mb: 0.5 }}>
                                            Set
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'white' }}>
                                            {previewCard.set?.name || '-'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Ocena */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: 'rgb(156, 163, 175)', mb: 0.5 }}>
                                        Ocena
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Rating
                                            value={previewCard.rating || 0}
                                            precision={0.5}
                                            size="large"
                                            readOnly
                                            sx={{
                                                '& .MuiRating-iconFilled': { color: 'rgb(245, 158, 11)' },
                                                '& .MuiRating-iconHover': { color: 'rgb(245, 158, 11)' }
                                            }}
                                        />
                                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                                            {previewCard.rating || 0}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Opis */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: 'rgb(156, 163, 175)', mb: 0.5 }}>
                                        Opis
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'white' }}>
                                        {previewCard.description || 'Brak opisu'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ borderTop: '1px solid rgba(76, 29, 149, 0.3)', p: 2 }}>
                    <Button
                        onClick={handleClosePreviewDialog}
                        sx={{ color: 'rgb(156, 163, 175)' }}
                    >
                        Zamknij
                    </Button>
                    {previewCard && (
                        <Button
                            onClick={() => {
                                handleClosePreviewDialog();
                                handleEditCard(previewCard);
                            }}
                            variant="contained"
                            startIcon={<EditIcon />}
                            sx={{
                                backgroundColor: 'rgb(76, 29, 149)',
                                '&:hover': { backgroundColor: 'rgb(91, 33, 182)' }
                            }}
                        >
                            Edytuj ocenę
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Mobile admin card preview */}
            {isMobile && mobilePreviewOpen && (
                <MobileAdminCardPreview
                    cards={filteredCards}
                    currentIndex={mobilePreviewIndex}
                    onClose={handleMobilePreviewClose}
                    onIndexChange={handleMobileIndexChange}
                    onSaveCard={handleSaveCard}
                    saving={saving}
                />
            )}

            {/* Desktop card preview */}
            {!isMobile && (
                <CardPreviewModal
                    card={hoveredCard}
                    position={hoverPosition}
                    onClose={handleCardLeave}
                />
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LimitedTierlistManager; 